import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { SEASONAL_MENUS, IMAGES } from "@/lib/content";

const SEASONS = ["Winter", "Spring", "Summer", "Autumn"];
const MEALS = ["breakfast", "lunch", "dinner", "dessert"];

export default function SeasonalMenus() {
  const [season, setSeason] = useState("Winter");
  const data = SEASONAL_MENUS[season];

  return (
    <section
      id="seasonal-menus"
      className="relative bg-bg py-28 md:py-40 border-t border-white/10"
      data-testid="seasonal-menus"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Reveal>
          <Overline>Seasonal Menus · Good food is personal</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-ink text-4xl md:text-6xl lg:text-7xl leading-[1] max-w-4xl">
            Menus tuned to the <em className="italic text-metallic-gold">season</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 font-sans text-base md:text-lg text-ink/70 max-w-2xl leading-relaxed">
            Every menu is designed around what&rsquo;s growing, hunted or in season across
            Central Otago — and around what the body needs. Every dish comes with its
            healing benefit.
          </p>
        </Reveal>

        {/* Season tabs */}
        <div className="mt-14 md:mt-16 flex flex-wrap gap-3 md:gap-4">
          {SEASONS.map((s) => (
            <button
              key={s}
              onClick={() => setSeason(s)}
              data-testid={`menu-season-${s.toLowerCase()}`}
              className={`rounded-full border px-7 py-3 font-sans text-xs tracking-[0.2em] uppercase transition-all duration-500 ${
                season === s
                  ? "border-gold text-metallic-gold bg-black/40"
                  : "border-white/15 text-ash hover:text-metallic-gold hover:border-gold/50"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Season content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={season}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16"
          >
            {/* Left: seasonal food photo + note */}
            <div className="lg:col-span-5">
              <div className="relative overflow-hidden aspect-[4/5]">
                <img
                  src={data.image}
                  alt={`${season} seasonal food`}
                  className="h-full w-full object-cover img-cinematic"
                />
                <div className="absolute inset-0 border border-white/10 pointer-events-none" />
              </div>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-ink/85 leading-snug max-w-md">
                {data.note}
              </p>
            </div>

            {/* Right: meals */}
            <div className="lg:col-span-7 space-y-14">
              {MEALS.map((meal) => (
                <div key={meal} data-testid={`menu-meal-${meal}`}>
                  <span className="font-sans text-[0.65rem] tracking-[0.35em] uppercase text-gold">
                    {meal}
                  </span>
                  {meal === "dessert" && (
                    <div className="mt-4 relative overflow-hidden aspect-[16/7]">
                      <img
                        src={IMAGES.foodDessert}
                        alt={`${season} desserts`}
                        className="h-full w-full object-cover img-cinematic"
                      />
                      <div className="absolute inset-0 border border-white/10 pointer-events-none" />
                    </div>
                  )}
                  <div className="mt-4 divide-y divide-white/10 border-t border-white/10">
                    {(data[meal] || []).map((dish) => (
                      <div
                        key={dish.name}
                        className="py-6 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8"
                      >
                        <div className="md:col-span-5">
                          <h3 className="font-serif text-2xl text-ink leading-tight">
                            {dish.name}
                          </h3>
                          <p className="mt-2 font-sans text-sm text-ash leading-relaxed">
                            {dish.detail}
                          </p>
                        </div>
                        <div className="md:col-span-7 md:pl-6 md:border-l md:border-white/10">
                          <span className="font-sans text-[0.6rem] tracking-[0.25em] uppercase text-forest-soft">
                            Healing benefit
                          </span>
                          <p className="mt-2 font-serif italic text-base md:text-lg text-ink/85 leading-relaxed">
                            {dish.benefit}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
